import { useEffect, useState } from "react";
import { Search, Save, Trash2, User, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "screening", "shortlisted", "interview", "offered", "hired", "rejected"];

export default function AdminTalentSearch() {
  const [f, setF] = useState({ q: "", skills: "", location: "", min_experience: 0, max_experience: 30, status: "", notice_period: "", source: "" });
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const params = { ...f };
      if (!params.status) delete params.status;
      if (!params.source) delete params.source;
      const r = await api.get("/admin/candidates/search", { params });
      setItems(r.data.items || []);
    } finally { setBusy(false); }
  };

  useEffect(() => { api.get("/admin/saved-searches").then((r) => setSaved(r.data.items || [])); }, []);
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const saveSearch = async () => {
    const name = window.prompt("Name this search:");
    if (!name) return;
    await api.post("/admin/saved-searches", { name, query: f });
    toast.success("Saved");
    api.get("/admin/saved-searches").then((r) => setSaved(r.data.items || []));
  };

  const loadSaved = (s) => { setF(s.query); setTimeout(search, 0); };

  const removeSaved = async (id) => {
    await api.delete(`/admin/saved-searches/${id}`);
    setSaved(saved.filter((s) => s.id !== id));
  };

  const downloadResume = async (a) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/applications/${a.id}/resume`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = a.resume_filename || "resume"; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-talent-search">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Talent Search</h1>
        <p className="text-sm text-slate-500">Boolean search across your entire candidate database + saved pools</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-12">
          <Input placeholder="Keyword (name, email, company, role, cover text)" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="md:col-span-6" data-testid="ts-q" />
          <Input placeholder="Skills (comma-separated)" value={f.skills} onChange={(e) => setF({ ...f, skills: e.target.value })} className="md:col-span-3" data-testid="ts-skills" />
          <Input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="md:col-span-3" />
          <Input type="number" placeholder="Min exp" value={f.min_experience} onChange={(e) => setF({ ...f, min_experience: parseFloat(e.target.value) || 0 })} className="md:col-span-2" />
          <Input type="number" placeholder="Max exp" value={f.max_experience} onChange={(e) => setF({ ...f, max_experience: parseFloat(e.target.value) || 100 })} className="md:col-span-2" />
          <Input placeholder="Notice period" value={f.notice_period} onChange={(e) => setF({ ...f, notice_period: e.target.value })} className="md:col-span-2" />
          <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
            <SelectTrigger className="md:col-span-3"><SelectValue placeholder="Any status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2 md:col-span-3 md:justify-end">
            <Button onClick={search} disabled={busy} data-testid="ts-search"><Search className="mr-1 h-4 w-4" />Search</Button>
            <Button variant="outline" onClick={saveSearch}><Save className="mr-1 h-4 w-4" />Save</Button>
          </div>
        </div>
      </div>

      {saved.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs uppercase tracking-widest text-slate-500">Saved pools:</span>
          {saved.map((s) => (
            <Badge key={s.id} className="cursor-pointer gap-1 bg-secondary/15 text-secondary hover:bg-secondary/25" onClick={() => loadSaved(s)}>
              {s.name}
              <button className="ml-1" onClick={(e) => { e.stopPropagation(); removeSaved(s.id); }}><Trash2 className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b bg-slate-50 px-4 py-2 text-xs text-slate-500">{items.length} candidate{items.length === 1 ? "" : "s"} matched</div>
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Candidate</th><th className="px-4 py-3">Current</th><th className="px-4 py-3">Exp</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Notice</th><th className="px-4 py-3">Tags</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`ts-result-${a.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-slate-900">{a.candidate_name}</div><div className="text-xs text-slate-500">{a.email} · {a.phone}</div></td>
                <td className="px-4 py-3 text-slate-700"><div>{a.current_company || "—"}</div><div className="text-xs text-slate-500">{a.job_title}</div></td>
                <td className="px-4 py-3">{a.experience_years} yr</td>
                <td className="px-4 py-3">{a.location || "—"}</td>
                <td className="px-4 py-3">{a.notice_period || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">{(a.tags || []).slice(0, 3).map((t) => <Badge key={t} className="bg-slate-100 text-slate-700">{t}</Badge>)}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin/candidates/${encodeURIComponent(a.email)}`}><Button variant="ghost" size="sm"><User className="h-3.5 w-3.5" /></Button></Link>
                  <Button variant="ghost" size="sm" onClick={() => downloadResume(a)}><Download className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No matches. Refine your search.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
